<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button type="button" class="btn btn-secondary" @click="handleHours">
          <i class="ri-time-line"></i>
          營業時間
        </button>
        <button type="button" class="btn btn-primary" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadStore">重試</button>
    </div>

    <div v-else-if="store" class="detail-content">
      <!-- 基本資訊 -->
      <div class="info-section">
        <h3 class="section-title">基本資訊</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">門市代碼：</span>
            <span class="info-value">{{ store.storeCode || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">門市名稱：</span>
            <span class="info-value">{{ store.storeName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">狀態：</span>
            <span :class="getStatusBadgeClass(store.status)">
              {{ getStatusText(store.status) }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">地址：</span>
            <span class="info-value">
              {{ [store.city, store.district, store.street].filter(Boolean).join('') || '-' }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">郵遞區號：</span>
            <span class="info-value">{{ store.postalCode || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">電話：</span>
            <span class="info-value">{{ store.phone || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Email：</span>
            <span class="info-value">{{ store.email || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">開幕日期：</span>
            <span class="info-value">{{ formatDate(store.openingDate) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">關閉日期：</span>
            <span class="info-value">{{ formatDate(store.closingDate) }}</span>
          </div>
        </div>
      </div>

      <!-- 門市設施 -->
      <div class="info-section">
        <h3 class="section-title">門市設施</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">容納人數：</span>
            <span class="info-value">{{ store.capacity || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">停車位數：</span>
            <span class="info-value">{{ store.parkingSpaces || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">門市面積（坪）：</span>
            <span class="info-value">{{ store.storeArea || '-' }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">設施說明：</span>
            <span class="info-value">{{ store.facilities || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 預約設定 -->
      <div class="info-section">
        <h3 class="section-title">預約設定</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">預約服務：</span>
            <span :class="store.bookingEnabled ? 'badge badge-success' : 'badge badge-secondary'">
              {{ store.bookingEnabled ? '啟用' : '停用' }}
            </span>
          </div>
          <template v-if="store.bookingEnabled">
            <div class="info-item">
              <span class="info-label">可預約天數：</span>
              <span class="info-value">{{ store.advanceBookingDays || '-' }} 天</span>
            </div>
            <div class="info-item">
              <span class="info-label">最少提前時數：</span>
              <span class="info-value">{{ store.minAdvanceHours || '-' }} 小時</span>
            </div>
            <div class="info-item">
              <span class="info-label">提前預約模式：</span>
              <span class="info-value">{{ store.advanceBookingMode === 'days' ? '提前多少天' : '提前多少小時' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">時段長度：</span>
              <span class="info-value">{{ store.slotDuration || '-' }} 分鐘</span>
            </div>
            <div class="info-item">
              <span class="info-label">每日最大預約數：</span>
              <span class="info-value">{{ store.maxDailyBookings || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">需要訂金：</span>
              <span :class="store.requireDeposit ? 'badge badge-warning' : 'badge badge-secondary'">
                {{ store.requireDeposit ? '是' : '否' }}
              </span>
            </div>
            <template v-if="store.requireDeposit">
              <div class="info-item">
                <span class="info-label">訂金金額：</span>
                <span class="info-value">NT$ {{ formatNumber(store.depositAmount) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">取消時限：</span>
                <span class="info-value">{{ store.cancellationHours || '-' }} 小時</span>
              </div>
            </template>
          </template>
        </div>
      </div>

      <!-- 營業時間複製 -->
      <div class="info-section">
        <h3 class="section-title">營業時間複製到其他分店</h3>
        <p class="copy-hours-hint">將此門市目前設定的營業時段，快速套用到其他分店。</p>
        <div v-if="targetStores.length > 0" class="copy-hours-box">
          <div class="copy-hours-actions">
            <button type="button" class="btn btn-secondary" @click="toggleSelectAllTargetStores">
              {{ selectedTargetStoreIds.length === targetStores.length ? '取消全選' : '全選分店' }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="copyingStores"
              @click="copyHoursToStores"
            >
              <span v-if="copyingStores">套用中...</span>
              <span v-else>套用到選取分店</span>
            </button>
          </div>
          <div class="store-checkbox-grid">
            <label v-for="item in targetStores" :key="item.id" class="store-checkbox-item">
              <input
                v-model="selectedTargetStoreIds"
                type="checkbox"
                :value="item.id"
              />
              <span>{{ item.storeName }}</span>
            </label>
          </div>
        </div>
        <div v-else class="copy-hours-empty">目前沒有其他可套用的分店</div>
      </div>

      <!-- 系統資訊 -->
      <div class="info-section">
        <h3 class="section-title">系統資訊</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">資料來源：</span>
            <span class="info-value">
              <span v-if="store.dataSource === 'wixtar'" class="badge badge-info">Wixtar 同步</span>
              <span v-else class="badge badge-secondary">自行新增</span>
            </span>
          </div>
          <div v-if="store.dataSource === 'wixtar'" class="info-item">
            <span class="info-label">最後同步：</span>
            <span class="info-value">{{ formatDateTime(store.wixtarSyncedAt) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">建立時間：</span>
            <span class="info-value">{{ formatDateTime(store.createdAt) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">最後更新：</span>
            <span class="info-value">{{ formatDateTime(store.updatedAt) }}</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { storeService } from '@/api/modules/stores.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'StoreDetail',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('門市詳情');
    const store = ref(null);
    const loading = ref(false);
    const copyingStores = ref(false);
    const error = ref(null);
    const targetStores = ref([]);
    const selectedTargetStoreIds = ref([]);
    const sourceStoreHours = ref([]);

    const loadStore = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await storeService.getStore(route.params.id);
        if (response && response.success && response.data) {
          store.value = response.data;
        } else {
          error.value = response && response.message || '獲取門市資料失敗';
        }
      } catch (err) {
        console.error('獲取門市資料失敗:', err);
        error.value = err.message || '獲取門市資料失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const loadTargetStores = async () => {
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 1000 });
        if (response && response.success && response.data?.items) {
          targetStores.value = response.data.items.filter(item => item.id !== route.params.id);
        }
      } catch (err) {
        console.error('載入可複製分店失敗:', err);
      }
    };

    const loadSourceStoreHours = async () => {
      try {
        const response = await storeService.getStoreHours(route.params.id);
        if (response && response.success && response.data?.hours) {
          sourceStoreHours.value = response.data.hours
            .filter(day => day.isOpen)
            .map(day => ({
              dayOfWeek: day.dayOfWeek,
              isOpen: true,
              timeSlots: (day.timeSlots || [])
                .filter(slot => slot && slot.openTime && slot.closeTime)
                .map(slot => ({
                  displayName: String(slot.displayName || '').trim(),
                  openTime: slot.openTime,
                  closeTime: slot.closeTime
                }))
            }));
        } else {
          sourceStoreHours.value = [];
        }
      } catch (err) {
        console.error('載入來源門市營業時間失敗:', err);
        sourceStoreHours.value = [];
      }
    };

    const toggleSelectAllTargetStores = () => {
      if (selectedTargetStoreIds.value.length === targetStores.value.length) {
        selectedTargetStoreIds.value = [];
        return;
      }
      selectedTargetStoreIds.value = targetStores.value.map(item => item.id);
    };

    const copyHoursToStores = async () => {
      if (selectedTargetStoreIds.value.length === 0) {
        await showError('請先選擇分店', '至少選擇一個目標分店');
        return;
      }

      copyingStores.value = true;
      try {
        await loadSourceStoreHours();
        const results = await Promise.allSettled(
          selectedTargetStoreIds.value.map(targetStoreId =>
            storeService.setStoreHours(targetStoreId, sourceStoreHours.value)
          )
        );
        const successCount = results.filter(item => item.status === 'fulfilled').length;
        const failCount = results.length - successCount;
        if (failCount === 0) {
          await showSuccess('套用成功', `已套用到 ${successCount} 間分店`);
        } else {
          await showError('部分分店套用失敗', `成功 ${successCount} 間，失敗 ${failCount} 間`);
        }
      } catch (err) {
        console.error('套用營業時間到分店失敗:', err);
        await showError('套用失敗', err.message || '請稍後再試');
      } finally {
        copyingStores.value = false;
      }
    };

    const handleBack = () => {
      router.push('/client/stores');
    };

    const handleEdit = () => {
      router.push(`/client/stores/${route.params.id}/edit`);
    };

    const handleHours = () => {
      router.push(`/client/stores/${route.params.id}/hours`);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatNumber = (number) => {
      if (number === null || number === undefined) return '0';
      return Number(number).toLocaleString('zh-TW');
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        active: 'badge badge-success',
        inactive: 'badge badge-danger',
        closed: 'badge badge-secondary',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        active: '啟用',
        inactive: '停用',
        closed: '已關閉',
      };
      return statusMap[status] || status;
    };

    onMounted(() => {
      loadStore();
      loadTargetStores();
      loadSourceStoreHours();
    });

    return {
      store,
      loading,
      copyingStores,
      error,
      targetStores,
      selectedTargetStoreIds,
      pageTitle,
      loadStore,
      toggleSelectAllTargetStores,
      copyHoursToStores,
      handleBack,
      handleEdit,
      handleHours,
      formatDate,
      formatDateTime,
      formatNumber,
      getStatusBadgeClass,
      getStatusText,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* Bootstrap 5 未提供 badge-info / badge-secondary 色階，於此補上來源標記樣式 */
.badge-info {
  background: #d1ecf1;
  color: #0c5460;
}

.badge-secondary {
  background: #e9ecef;
  color: #495057;
}

.detail-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: flex-start;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-weight: 500;
  color: #6b7280;
  min-width: 120px;
  margin-right: 8px;
}

.info-value {
  color: #111827;
  flex: 1;
}

.copy-hours-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
}

.copy-hours-box {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 12px;
}

.copy-hours-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.store-checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}

.store-checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
}

.copy-hours-empty {
  color: #9ca3af;
  font-size: 13px;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background-color: #d1fae5;
  color: #065f46;
}

.badge-danger {
  background-color: #fee2e2;
  color: #991b1b;
}

.badge-secondary {
  background-color: #e5e7eb;
  color: #374151;
}

.badge-warning {
  background-color: #fef3c7;
  color: #92400e;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #ef4444;
  margin-bottom: 16px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background-color: #2563eb;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
